import React, { Suspense, lazy } from "react";
import { Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import { consumeReturnUrl, rememberReturnUrl } from "./returnUrl";
import { useSelector } from "react-redux";
import { CircularProgress, Grid2 as Grid } from "@mui/material";
import PageLayout from "../components/PageLayout";
import {WebSocketProvider} from "./websocket";

// Eagerly loaded core pages
import Home from "../pages/home/Home";
import Login from "../pages/auth/Login";
import LogoutPage from "../pages/auth/Logout";
import { NotFound } from "../pages/error/NotFound";

// Lazy-loaded secondary & heavy pages
const Places = lazy(() => import("../pages/places/Places"));
const Place = lazy(() => import("../pages/places/place/Place"));
const TicketsActive = lazy(() => import("../pages/tickets/TicketsActive"));
const Wallet = lazy(() => import("../pages/wallet/Wallet"));
const PlacesMap = lazy(() => import("../pages/places/map/PlacesMap"));
const Support = lazy(() => import("../pages/support/Support"));
const SupportDetail = lazy(() => import("../pages/support/supportDetail/SupportDetail"));
const UserBasket = lazy(() => import("../pages/basket/UserBasket"));
const Settings = lazy(() => import("../pages/settings/Settings"));
const Checkout = lazy(() => import("../pages/checkout/Checkout"));
const Survey = lazy(() => import("../pages/survey/Survey"));
const InviteFriends = lazy(() => import("../pages/inviteFriends/InviteFriends"));
const EditProfile = lazy(() => import("../pages/editProfile/EditProfile"));
const SingleSubscribe = lazy(() => import("../pages/tickets/singleSubscribe/SingleSubscribe"));
const SingleCourse = lazy(() => import("../pages/tickets/singleCourse/SingleCourse"));
const Psych = lazy(() => import("../pages/Psych/Psych"));
const Coaches = lazy(() => import("../pages/coaches/Coaches"));
const Coach = lazy(() => import("../pages/coaches/coach/Coach"));
const Notifs = lazy(() => import("../pages/notifs/Notifs"));
const Page = lazy(() => import("../pages/home/Page"));
const Code = lazy(() => import("../pages/auth/Code"));
const Favorites = lazy(() => import("../pages/favorites/Favorites"));

function RedirectToLogin() {
    const location = useLocation();
    rememberReturnUrl(location.pathname, location.search);
    return <Navigate to="/login" replace />;
}

function RedirectAfterLogin() {
    return <Navigate to={consumeReturnUrl()} replace />;
}

const PageFallback = () => (
    <Grid container sx={{ width: "100%", height: "70vh" }} justifyContent="center" alignItems="center">
        <CircularProgress color="primary" />
    </Grid>
);

export default function ApplicationRoutes() {
    const isAuthorized = useSelector(({ auth: { user } }) => (user ? user.Id != null : false));

    function DynamicRedirect({ to }) {
        const params = useParams();
        const resolvedPath = Object.keys(params).reduce(
            (path, param) => path.replace(`:${param}`, params[param]),
            to
        );
        return <Navigate to={resolvedPath} replace />;
    }

    return (
        <Suspense fallback={<PageFallback />}>
            {isAuthorized && (
                <WebSocketProvider>
                <PageLayout>
                    <Routes>
                        <Route path="/" element={<Home />} />
                        <Route path="/page/:pData" element={<Page />} />
                        <Route path="/places" element={<Places />} />
                        <Route path="/places/:sid" element={<Places />} />
                        <Route path="/place/:placeId" element={<Place />} />
                        <Route path="/placesMap" element={<PlacesMap />} />
                        <Route path="/favorites" element={<Favorites />} />
                        <Route path="/coaches" element={<Coaches />} />
                        <Route path="/profile/survey" element={<Survey />} />
                        <Route path="/profile/invitefriends" element={<InviteFriends />} />
                        <Route path="/profile/edit" element={<EditProfile />} />
                        <Route path="/support" element={<Support />} />
                        <Route path="/support/detail/:supportId" element={<SupportDetail />} />
                        <Route path="/Psych" element={<Psych />} />
                        {/* tickets */}
                        <Route path="/tickets" element={<TicketsActive />} />
                        <Route path="/ticketsHistory" element={<Navigate to="/tickets?tab=history" replace />} />
                        <Route path="/tickets/singleSubscribe/:subscribeKey" element={<SingleSubscribe />} />
                        <Route path="/tickets/singleCourse/:courseKey" element={<SingleCourse />} />
                        <Route path="/wallet" element={<Wallet />} />
                        <Route path="/wallet/:code" element={<Wallet />} />
                        <Route path="/UserRequests" element={<Navigate to="/wallet?tab=requests" replace />} />
                        <Route path="/checkout/:formData" element={<Checkout />} />
                        <Route path="/notifs" element={<Notifs />} />
                        <Route path="/invoices" element={<Navigate to="/tickets?tab=invoices" replace />} />
                        <Route path="/settings" element={<Settings />} />
                        <Route path="/basket" element={<UserBasket />} />
                        <Route path="/coach/:coachId" element={<Coach />} />
                        <Route path="/auth/*" element={<RedirectAfterLogin />} />
                        <Route path="/login" element={<RedirectAfterLogin />} />
                        <Route path="/login/:code" element={<RedirectAfterLogin />} />
                        <Route path="/logout" element={<LogoutPage />} />
                        <Route path="/404" element={<NotFound />} />
                        <Route path="/code/:code" element={<DynamicRedirect to={"/wallet/:code"} />} />
                        <Route path="/*" element={<Navigate to="/404" replace />} />
                    </Routes>
                </PageLayout>
                </WebSocketProvider>
            )}

            {!isAuthorized && (
                <Routes>
                    <Route path="/place/:placeId" element={<Place />} />
                    <Route path="/code/:code" element={<Code />} />
                    <Route path="/coach/:coachId" element={<Coach />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/login/:code" element={<Login />} />
                    <Route path="/logout" element={<Navigate to={"/"} />} />
                    <Route path="/*" element={<RedirectToLogin />} />
                </Routes>
            )}
        </Suspense>
    );
}
